<script>
    import Spinner from 'svelte-spinner';
    import { Tooltip } from "sveltestrap";
    
    import { vptServer, token } from '../../stores.js';
    import { countActiveIsolationElements } from '../../config';
    
    import Badge from '../../component/Badge.svelte';
    
    export let id;
    export let todaysConfig;
    export let selectedInmateAspect;

    let isolationRecord;
    let elementCount;

    $: {
        if (todaysConfig) {
            isolationRecord = null;
            getIsolationRecord();
        }
    }

    async function getIsolationRecord() {
        await fetch(`${$vptServer}/inmate/${id}/isolation/overview`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            isolationRecord = data;
            elementCount = countActiveIsolationElements(todaysConfig);
        })
        .catch(error => {
            console.error("Error getting inmate isolation overview");
            console.error(error);
            return;
        });
    }
</script>

<style>
    .inmate-widget {
        width: 240px;
    }

    .inmate-widget.low-element-count {
        width: 160px;
    }

    .inmate-widget .no-records {
        padding: 10px 10px 0 10px;
    }

    .isolation-widget {
        padding: 5px 3px 0 5px;
        width: 100%;
    }

    .isolation-widget.high-element-count {
        padding-right: 0;
    }

    .isolation-widget.single-element {
        text-align: center;
    }

    .badge-container {
        display: inline-block;
        width: 32%;
        padding: 0 2px;
        margin-bottom: 5px;
        text-align: center;
    }

    .high-element-count .badge-container {
        width: 23%;
        padding: 0;
    }

    .low-element-count .badge-container {
        width: 48%;
    }

    :global(.isolation-widget .badge) {
        width: 100% !important;
        font-size: 10.5px !important;
    }

    .badge-label {
        display: inline-block;
        width: 100%;
        font-size: 10.5px;
        font-weight: 700;
        text-align: center;
        white-space: nowrap;
    }
</style>

<div class="inmate-widget" class:low-element-count={elementCount < 5}>
    {#if isolationRecord}
        <button class="button-reset focus-outline card" on:click class:selected={selectedInmateAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    {#if isolationRecord.imsUrn}
                        <span class="widget-label" class:widget-label-targeted={isolationRecord.indicatorTargeted}
                                class:widget-label-trend={isolationRecord.indicatorTrend == 'U' || isolationRecord.indicatorTrend == 'D'}>
                            {#if isolationRecord.indicatorTargeted}
                                <img id="targeted-indicator-isolation" src="/assets/target-indicator.svg" alt="Targeted on the Isolation aspect" />
                                <Tooltip target="targeted-indicator-isolation" placement="bottom">Targeted on the Isolation aspect</Tooltip>
                            {/if}
                            Isolation
                            {#if isolationRecord.indicatorTrend == 'U'}
                                <img id="trend-indicator-isolation-increase" src="/assets/arrow/arrow-up-danger.svg" class="widget-trend-arrow" alt="Combined isolation level has increased compared to their last {todaysConfig.trendPastDaysToAnalyse} day{todaysConfig.trendPastDaysToAnalyse > 1 ? 's' : ''} average" />
                                <Tooltip target="trend-indicator-isolation-increase" placement="bottom">Combined level of isolation has increased compared to their last {todaysConfig.trendPastDaysToAnalyse} day{todaysConfig.trendPastDaysToAnalyse > 1 ? 's' : ''} average</Tooltip>
                            {:else if isolationRecord.indicatorTrend == 'D'}
                                <img id="trend-indicator-isolation-decrease" src="/assets/arrow/arrow-down-success.svg" class="widget-trend-arrow" alt="Combined isolation level has decreased compared to their last {todaysConfig.trendPastDaysToAnalyse} day{todaysConfig.trendPastDaysToAnalyse > 1 ? 's' : ''} average" />
                                <Tooltip target="trend-indicator-isolation-decrease" placement="bottom">Combined level of isolation has decreased compared to their last {todaysConfig.trendPastDaysToAnalyse} day{todaysConfig.trendPastDaysToAnalyse > 1 ? 's' : ''} average</Tooltip>
                            {/if}
                        </span>

                        <div class="widget-content">
                            <div class="isolation-widget" class:high-element-count={elementCount > 6} class:single-element={elementCount == 1}>
                                {#if !(todaysConfig.isolationVisitWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-visit" score={isolationRecord.levelVisit} label={todaysConfig.isolationVisitLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedVisit} recentChange={isolationRecord.indicatorLevelChangedToHighVisit}
                                            lastDate={isolationRecord.dateLastVisit} attendedLabel="Visited" />
                                        <span class="badge-label">{isolationRecord.labelLastVisit ? isolationRecord.labelLastVisit : ""}</span>
                                    </div>
                                {/if} 

                                {#if !(todaysConfig.isolationWorkWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-work" score={isolationRecord.levelWork} label={todaysConfig.isolationWorkLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedWork} recentChange={isolationRecord.indicatorLevelChangedToHighWork}
                                            lastDate={isolationRecord.dateLastWork} />
                                        <span class="badge-label">{isolationRecord.labelLastWork ? isolationRecord.labelLastWork : ""}</span>
                                    </div>
                                {/if}

                                {#if !(todaysConfig.isolationPhoneWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-phone" score={isolationRecord.levelPhone} label={todaysConfig.isolationPhoneLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedPhone} recentChange={isolationRecord.indicatorLevelChangedToHighPhone}
                                            lastDate={isolationRecord.dateLastPhone} attendedLabel="Top Up" />
                                        <span class="badge-label">{isolationRecord.labelLastPhone ? isolationRecord.labelLastPhone : ""}</span>
                                    </div>
                                {/if}

                                {#if !(todaysConfig.isolationMealWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-meal" score={isolationRecord.levelMeal} label={todaysConfig.isolationMealLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedMeal} recentChange={isolationRecord.indicatorLevelChangedToHighMeal}
                                            lastDate={isolationRecord.dateLastMeal}  attendedLabel="Selected" />
                                        <span class="badge-label">{isolationRecord.labelLastMeal ? isolationRecord.labelLastMeal : ""}</span>
                                    </div>
                                {/if}

                                {#if !(todaysConfig.isolationCanteenWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-canteen" score={isolationRecord.levelCanteen} label={todaysConfig.isolationCanteenLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedCanteen} recentChange={isolationRecord.indicatorLevelChangedToHighCanteen}
                                            lastDate={isolationRecord.dateLastCanteen} attendedLabel="Purchased"
                                            additionalTooltipText={isolationRecord.levelCanteen < 3 ? `${todaysConfig.inmateLabelName} spent ${isolationRecord.levelCanteen == 1 ? "significantly" : ""} less than their 7 day average` : null} />
                                        <span class="badge-label">{isolationRecord.labelLastCanteen ? isolationRecord.labelLastCanteen : ""}</span>
                                    </div>
                                {/if}

                                {#if !(todaysConfig.isolationGymWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-gym" score={isolationRecord.levelGym} label={todaysConfig.isolationGymLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedGym} recentChange={isolationRecord.indicatorLevelChangedToHighGym}
                                            lastDate={isolationRecord.dateLastGym} />
                                        <span class="badge-label">{isolationRecord.labelLastGym ? isolationRecord.labelLastGym : ""}</span>
                                    </div>
                                {/if}

                                {#if !(todaysConfig.isolationPaWeighting === 0)}
                                    <div class="badge-container">
                                        <Badge id="level-activity" score={isolationRecord.levelActivity} label={todaysConfig.isolationPaLabel}
                                            changeDate={isolationRecord.dateLevelLastChangedActivity} recentChange={isolationRecord.indicatorLevelChangedToHighActivity}
                                            lastDate={isolationRecord.dateLastActivity} />
                                        <span class="badge-label">{isolationRecord.labelLastActivity ? isolationRecord.labelLastActivity : ""}</span>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {:else}
                        <span class="widget-label">
                            Isolation
                        </span>
                        <div class="widget-content">
                            <span class="no-records">No Isolation data is available for today.</span>
                        </div>
                    {/if}
                </div>
            </div>
        </button>
    {:else}
        <div class="card" class:selected={selectedInmateAspect === "Isolation"}>
            <div class="card-body">
                <div class="widget-container">
                    <div class="widget-spinner">
                        <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>  